import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Stack, Tab, Tabs, TextField, Tooltip,
  Typography,
} from '@mui/material';
import { Save, Warning } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { shortsAPI, ShortsClip } from '../../services/api';

interface Props {
  clip: ShortsClip | null;
  onClose: () => void;
}

/** Statuses after which a clip is live somewhere and editing is misleading. */
const LIVE = new Set(['published']);
/** Statuses where a platform already has the content and our copy won't reach it. */
const HANDED_OVER = new Set(['scheduled', 'uploading']);

const ShortsClipEditor: React.FC<Props> = ({ clip, onClose }) => {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [when, setWhen] = useState('');
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (!clip) return;
    setTitle(clip.title ?? '');
    setDescription(clip.description ?? '');
    // datetime-local wants `YYYY-MM-DDTHH:mm` in LOCAL time.
    setWhen(clip.scheduled_at
      ? new Date(clip.scheduled_at).toLocaleString('sv').slice(0, 16).replace(' ', 'T')
      : '');
    setTab(0);
    setNotMoved({});
  }, [clip]);

  const dirty = !!clip && (
    title !== (clip.title ?? '') ||
    description !== (clip.description ?? '') ||
    when !== (clip.scheduled_at
      ? new Date(clip.scheduled_at).toLocaleString('sv').slice(0, 16).replace(' ', 'T')
      : ''));

  // Preview the UNSAVED text, so the editor sees what will actually post.
  const preview = useQuery({
    queryKey: ['shorts', 'preview', clip?.clip_id, title, description],
    queryFn: () => shortsAPI.previewClip(clip!.clip_id, { title, description }),
    enabled: !!clip,
    staleTime: 2000,
  });

  // Platforms whose time could not be moved, from the last save. Shown until
  // the next edit: "saved" on its own would imply the release actually moved.
  const [notMoved, setNotMoved] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: () => shortsAPI.patchClip(clip!.clip_id, {
      title,
      description,
      ...(when ? { scheduled_at: new Date(when).toISOString() } : {}),
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['shorts', 'batch'] });
      qc.invalidateQueries({ queryKey: ['shorts', 'batches'] });
      // Only `pending` targets move. A platform that already holds the file is
      // on its OWN timer, so closing with a cheerful "saved" would let someone
      // believe they moved a release that is going out at the old time.
      const stuck = res.targets_not_moved || {};
      if (Object.keys(stuck).length > 0) {
        setNotMoved(stuck);
        return;          // hold the dialog open so the warning is read
      }
      setNotMoved({});
      onClose();
    },
  });

  const targets = clip?.targets ?? [];
  const live = targets.filter((t) => LIVE.has(t.status));
  const handedOver = targets.filter((t) => HANDED_OVER.has(t.status));
  const movable = targets.filter((t) => t.status === 'pending');

  const platforms = useMemo(
    () => Object.keys(preview.data?.previews ?? {}), [preview.data]);
  const current = platforms[tab];
  const shown = current ? preview.data?.previews[current] : undefined;

  return (
    <Dialog open={!!clip} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit clip
        {clip && (
          <Typography variant="caption" display="block" color="text.secondary">
            {clip.filename} · {clip.width}×{clip.height} ·{' '}
            {clip.duration_seconds ? `${clip.duration_seconds.toFixed(1)}s` : '—'}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {Object.keys(notMoved).length > 0 && (
          <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
            <AlertTitle>Saved — but some platforms did not move</AlertTitle>
            The new time applies to everything still waiting. These already
            have the clip, so they publish on their own timer:
            <Box component="ul" sx={{ pl: 2.5, mb: 0, mt: 0.5 }}>
              {Object.entries(notMoved).map(([p, why]) => (
                <li key={p}>
                  <strong>{p}</strong> — {why}
                </li>
              ))}
            </Box>
          </Alert>
        )}
        <Stack spacing={2}>
          {/* Editing something already public is misleading — our copy changes,
              the live post doesn't. */}
          {live.length > 0 && (
            <Alert severity="warning" icon={<Warning />}>
              Already published on {live.map((t) => t.platform).join(', ')}.
              Editing here updates AutoPod's record — it does <strong>not</strong>{' '}
              change the live post. Fix those by hand on the platform.
            </Alert>
          )}
          {handedOver.length > 0 && (
            <Alert severity="info">
              {handedOver.map((t) => t.platform).join(', ')} already{' '}
              {handedOver.length === 1 ? 'has' : 'have'} the clip and{' '}
              {handedOver.length === 1 ? 'holds' : 'hold'} the release timer, so a
              new time won't reach {handedOver.length === 1 ? 'it' : 'them'}.
              {movable.length > 0 && ` ${movable.length} pending target(s) will move.`}
            </Alert>
          )}
          {preview.data && !preview.data.episode_url_resolved && (
            <Alert severity="warning">
              No episode link resolved for this clip — captions will omit the
              “Watch the ENTIRE video episode” line. Usually means the episode
              code is wrong, or that episode hasn't been published to YouTube
              through AutoPod yet.
            </Alert>
          )}

          <TextField
            label="Title" value={title} size="small" fullWidth
            onChange={(e) => setTitle(e.target.value)}
            helperText={`${title.length}/100 — the YouTube Short title, and the Reddit post title`}
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            label="Extra description" value={description} size="small" fullWidth
            multiline minRows={2}
            onChange={(e) => setDescription(e.target.value)}
            helperText="Folded into every caption. The episode link, links block and hashtags are added automatically."
          />
          <TextField
            label="Release time" type="datetime-local" size="small"
            value={when} onChange={(e) => setWhen(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText={movable.length
              ? `Moves ${movable.length} pending target(s)`
              : 'No pending targets — nothing to move'}
            sx={{ maxWidth: 280 }}
          />

          <Divider />

          <Box>
            <Typography variant="caption" color="text.secondary">
              CAPTION PREVIEW — exactly what posts
            </Typography>
            {preview.isLoading && <Box sx={{ p: 2 }}><CircularProgress size={18} /></Box>}
            {platforms.length > 0 && (
              <>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}
                      variant="scrollable" scrollButtons="auto"
                      sx={{ minHeight: 34, '& .MuiTab-root': { minHeight: 34, fontSize: 12 } }}>
                  {platforms.map((p) => <Tab key={p} label={p} />)}
                </Tabs>
                {shown && (
                  <Box sx={{ mt: 1 }}>
                    {shown.post_title !== null && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          POST TITLE
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {shown.post_title || <em>(empty)</em>}
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {current === 'reddit' ? 'BODY (first comment)' : 'CAPTION / DESCRIPTION'}
                    </Typography>
                    <Box component="pre" sx={{
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      fontSize: 12, m: 0, p: 1.5, maxHeight: 260, overflow: 'auto',
                      border: '1px solid', borderColor: 'divider', borderRadius: 1,
                    }}>
                      {shown.body}
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                      <Chip size="small" variant="outlined"
                            label={`${shown.body.length} chars`} />
                      {current === 'reddit' && (
                        <Tooltip title="Reddit renders a line-leading # as a heading, so hashtags are escaped. The body also omits the title, because the title IS the post title.">
                          <Chip size="small" variant="outlined" label="reddit formatting" />
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained" startIcon={save.isPending ? <CircularProgress size={14} /> : <Save />}
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShortsClipEditor;
